import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ArrowUp,
  Loader2,
  Plus,
  Paperclip,
  Camera,
  X,
  AlertCircle,
} from 'lucide-angular';
import { PendingAttachment } from '../../grading.types';
import { ConfirmDialogComponent } from '../../../../../common/components/confirm-dialog/confirm-dialog';
import { InputAttachmentsPreviewComponent } from '../input-attachments-preview/input-attachments-preview.component';

/** Allowed MIME types for file upload */
const ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
  'image/tif',
  'image/bmp',
  'image/avif',
  'application/pdf',
];

/** Max file size in bytes (10 MB) */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, ConfirmDialogComponent, InputAttachmentsPreviewComponent],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputComponent {
  readonly disabled = input<boolean>(false);
  readonly placeholder = input<string>('Type your message...');
  readonly pendingAttachments = input<PendingAttachment[]>([]);

  readonly messageSent = output<string>();
  readonly filesSelected = output<File[]>();
  readonly attachmentRemoved = output<string>();

  protected readonly textareaRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  protected readonly fileInputRef =
    viewChild<ElementRef<HTMLInputElement>>('fileInput');
  protected readonly cameraInputRef =
    viewChild<ElementRef<HTMLInputElement>>('cameraInput');
  protected readonly videoRef =
    viewChild<ElementRef<HTMLVideoElement>>('videoPreview');
  protected readonly canvasRef =
    viewChild<ElementRef<HTMLCanvasElement>>('captureCanvas');
  protected readonly confirmDialog =
    viewChild<ConfirmDialogComponent>('confirmDialog');

  protected readonly icons = {
    ArrowUp,
    Loader2,
    Plus,
    Paperclip,
    Camera,
    X,
    AlertCircle,
  };

  protected messageText = '';
  protected isAttachmentMenuOpen = false;

  /**
   * Set the message text from outside the component
   */
  setMessage(text: string): void {
    this.messageText = text;
    // Trigger resize after setting text
    setTimeout(() => this.onInput(), 0);
  }
  protected isCameraOpen = false;
  protected cameraStream: MediaStream | null = null;
  protected cameraError: string | null = null;
  protected attachmentToRemove: PendingAttachment | null = null;

  /**
   * Check if any attachments are still uploading or processing
   */
  protected get hasProcessingAttachments(): boolean {
    return this.pendingAttachments().some(
      (a) => a.uploadState === 'pending' || a.uploadState === 'uploading' || a.uploadState === 'processing',
    );
  }

  /**
   * Check if send button should be enabled
   */
  protected get canSend(): boolean {
    const hasText = this.messageText.trim().length > 0;
    const hasCompletedAttachments = this.pendingAttachments().some(
      (a) => a.uploadState === 'completed',
    );
    // Disable sending while any attachments are still being processed
    return (hasText || hasCompletedAttachments) && !this.disabled() && !this.hasProcessingAttachments;
  }

  protected sendMessage(): void {
    if (!this.canSend) {
      return;
    }

    this.messageSent.emit(this.messageText.trim());
    this.messageText = '';

    // Reset textarea height
    const textarea = this.textareaRef()?.nativeElement;
    if (textarea) {
      textarea.style.height = 'auto';
    }
  }

  protected onInput(): void {
    // Auto-resize textarea
    const textarea = this.textareaRef()?.nativeElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }

  protected onEnterKey(event: KeyboardEvent): void {
    // Send on Enter (without Shift)
    if (!event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  protected toggleAttachmentMenu(event: Event): void {
    event.stopPropagation();
    this.isAttachmentMenuOpen = !this.isAttachmentMenuOpen;
  }

  protected onAddFiles(): void {
    this.isAttachmentMenuOpen = false;
    // Trigger file input click
    const fileInput = this.fileInputRef()?.nativeElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  protected onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    const validFiles = this.validateFiles(files);

    if (validFiles.length > 0) {
      this.filesSelected.emit(validFiles);
    }

    // Reset input so same file can be selected again
    input.value = '';
  }

  protected onTakePhoto(): void {
    this.isAttachmentMenuOpen = false;

    // On iOS/Safari, use native camera picker for better compatibility
    if (this.isIOSOrSafari()) {
      const cameraInput = this.cameraInputRef()?.nativeElement;
      if (cameraInput) {
        cameraInput.click();
      }
      return;
    }

    // On other platforms, use getUserMedia for in-app camera
    this.cameraError = null;
    this.isCameraOpen = true;

    // Use requestAnimationFrame to ensure DOM is updated before accessing video element
    requestAnimationFrame(() => {
      this.initializeCamera();
    });
  }

  protected onCameraInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    const validFiles = this.validateFiles(files);

    if (validFiles.length > 0) {
      this.filesSelected.emit(validFiles);
    }

    // Reset input so same file can be selected again
    input.value = '';
  }

  private isIOSOrSafari(): boolean {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isIOS || isSafari;
  }

  private async initializeCamera(): Promise<void> {
    try {
      // Request camera access
      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Prefer back camera
        audio: false,
      });

      const video = this.videoRef()?.nativeElement;
      if (video && this.cameraStream) {
        video.srcObject = this.cameraStream;
        // Use play() with promise handling for Safari
        try {
          await video.play();
        } catch (playError) {
          console.warn('Video autoplay failed, user interaction may be needed:', playError);
        }
      }
    } catch (error) {
      console.error('Failed to access camera:', error);
      this.cameraError =
        'Could not access camera. Please check permissions and try again.';
    }
  }

  protected capturePhoto(): void {
    const video = this.videoRef()?.nativeElement;
    const canvas = this.canvasRef()?.nativeElement;

    if (!video || !canvas) {
      return;
    }

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);

      // Convert to blob and create file
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File(
              [blob],
              `photo-${Date.now()}.jpg`,
              { type: 'image/jpeg' },
            );
            this.filesSelected.emit([file]);
          }
          this.closeCamera();
        },
        'image/jpeg',
        0.9,
      );
    }
  }

  protected closeCamera(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach((track) => track.stop());
      this.cameraStream = null;
    }
    this.isCameraOpen = false;
    this.cameraError = null;
  }

  protected requestRemoveAttachment(attachment: PendingAttachment): void {
    this.attachmentToRemove = attachment;
    const dialog = this.confirmDialog();
    if (dialog) {
      dialog.isOpen = true;
    }
  }

  protected onConfirmRemove(): void {
    if (this.attachmentToRemove) {
      this.attachmentRemoved.emit(this.attachmentToRemove.localId);
      this.attachmentToRemove = null;
    }
  }

  protected onCancelRemove(): void {
    this.attachmentToRemove = null;
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.isAttachmentMenuOpen = false;
  }

  private validateFiles(files: File[]): File[] {
    return files.filter((file) => {
      // Check MIME type
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        console.warn(`File type not allowed: ${file.type}`);
        return false;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File too large: ${file.name} (${file.size} bytes)`);
        return false;
      }

      return true;
    });
  }
}
